import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserRound, Plus, Trash2, Pencil, Briefcase, MessageCircle, Star } from "lucide-react";
import { toast } from "sonner";

const tones = [
  { ring: "ring-emerald-200", chip: "bg-emerald-100 text-emerald-700", accent: "from-emerald-50" },
  { ring: "ring-amber-200", chip: "bg-amber-100 text-amber-700", accent: "from-amber-50" },
  { ring: "ring-sky-200", chip: "bg-sky-100 text-sky-700", accent: "from-sky-50" },
  { ring: "ring-violet-200", chip: "bg-violet-100 text-violet-700", accent: "from-violet-50" },
  { ring: "ring-rose-200", chip: "bg-rose-100 text-rose-700", accent: "from-rose-50" },
  { ring: "ring-teal-200", chip: "bg-teal-100 text-teal-700", accent: "from-teal-50" },
];

const emptyForm = { name: "", specialty: "", whatsapp: "", available: true };

function WhatsappIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 016.988 2.9 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.463 3.488" />
    </svg>
  );
}

function Stars({ value = 0, count = 0, onRate, testid }) {
  const [hover, setHover] = useState(0);
  const interactive = !!onRate;
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <div className="flex" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => {
          const active = (hover || Math.round(value)) >= n;
          return (
            <button key={n} type="button" disabled={!interactive}
              data-testid={interactive ? `star-${n}` : undefined}
              onMouseEnter={() => interactive && setHover(n)}
              onClick={() => interactive && onRate(n)}
              className={`${interactive ? "cursor-pointer" : "cursor-default"} p-0.5`}>
              <Star className={`h-4 w-4 ${active ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            </button>
          );
        })}
      </div>
      <span className="text-xs text-slate-500">{value > 0 ? `${value} (${count})` : "لا تقييم"}</span>
    </div>
  );
}

export default function Consultants() {
  const { user } = useAuth();
  const canManage = user.role === "admin" || user.role === "supervisor";
  const isStudent = user.role === "student";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/consultants").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, specialty: c.specialty || "", whatsapp: c.whatsapp || "", available: c.available !== false }); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("اسم المستشار مطلوب"); return; }
    const payload = { name: form.name.trim(), specialty: form.specialty.trim(), whatsapp: form.whatsapp.trim(), available: form.available };
    try {
      if (editing) await api.put(`/consultants/${editing.id}`, payload);
      else await api.post("/consultants", payload);
      toast.success(editing ? "تم تحديث المستشار" : "تمت إضافة المستشار");
      setOpen(false); setForm(emptyForm); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذا المستشار؟")) return;
    try { await api.delete(`/consultants/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleAvailability = async (c) => {
    try { await api.put(`/consultants/${c.id}/availability`, { available: !(c.available !== false) }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rate = async (id, stars) => {
    try { await api.post(`/consultants/${id}/rate`, { stars }); toast.success("شكرًا لتقييمك"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openWhatsapp = (c) => {
    const digits = (c.whatsapp || "").replace(/\D/g, "");
    if (!digits) { toast.error("لا يوجد رقم واتساب لهذا المستشار"); return; }
    const msg = `السلام عليكم${c.name ? ` ${c.name}` : ""}، أنا طالب في مدينة حلب وأرغب في الحصول على استشارة بخصوص: `;
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  if (items === null) return <PageLoader />;

  return (
    <>
      <PageHeader title="دليل المستشارين" subtitle="تواصل مباشرة مع المستشارين المتاحين عبر واتساب — الأعلى تقييمًا أولًا"
        action={canManage && (
          <Button data-testid="add-consultant-btn" onClick={openCreate} className="rounded-xl bg-green-deep text-white hover:opacity-90">
            <Plus className="me-2 h-4 w-4" /> إضافة مستشار
          </Button>
        )}
      />

      {items.length === 0 ? (
        <EmptyState icon={UserRound} title="لا يوجد مستشارون بعد" desc={canManage ? "أضف أول مستشار ليظهر لجميع الطلاب." : "لم تتم إضافة أي مستشار حتى الآن."} />
      ) : (
        <div className="stagger grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c, i) => {
            const t = tones[i % tones.length];
            const available = c.available !== false;
            return (
              <div key={c.id} data-testid={`consultant-${c.id}`}
                className={`card-hover group relative overflow-hidden rounded-2xl border border-slate-100 bg-card p-6 soft-shadow ring-1 ${t.ring}`}>
                <div className={`pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b ${t.accent} to-transparent`} />
                <div className="relative flex items-start justify-between">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${t.chip}`}><UserRound className="h-6 w-6" /></div>
                  {canManage && (
                    <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button onClick={() => openEdit(c)} data-testid={`edit-consultant-${c.id}`} size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-navy"><Pencil className="h-4 w-4" /></Button>
                      <Button onClick={() => remove(c.id)} data-testid={`del-consultant-${c.id}`} size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>

                <div className="relative mt-4 flex items-center gap-2">
                  <h3 className="font-heading text-lg font-bold text-navy">{c.name}</h3>
                  <span data-testid={`availability-${c.id}`}
                    className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${available ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${available ? "bg-emerald-500" : "bg-slate-400"}`} />
                    {available ? "متاح الآن" : "مشغول"}
                  </span>
                </div>

                {c.specialty && (
                  <p className="relative mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                    <Briefcase className="h-3.5 w-3.5 text-slate-400" /> {c.specialty}
                  </p>
                )}

                <div className="relative mt-3">
                  <Stars value={c.avg_rating || 0} count={c.rating_count || 0} testid={`rating-${c.id}`} />
                </div>

                {isStudent && (
                  <div className="relative mt-3 rounded-xl bg-slate-50 px-3 py-2">
                    <p className="mb-1 text-xs text-slate-400">{c.my_rating ? "تقييمك:" : "قيّم هذا المستشار:"}</p>
                    <Stars value={c.my_rating || 0} count={0} onRate={(s) => rate(c.id, s)} testid={`my-rating-${c.id}`} />
                  </div>
                )}

                {canManage && (
                  <div className="relative mt-3 flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                    <span className="text-xs text-slate-500">الحالة: {available ? "متاح" : "مشغول"}</span>
                    <Switch data-testid={`toggle-availability-${c.id}`} checked={available} onCheckedChange={() => toggleAvailability(c)} />
                  </div>
                )}

                <Button data-testid={`wa-consultant-${c.id}`} onClick={() => openWhatsapp(c)} disabled={!c.whatsapp}
                  className="relative mt-4 w-full rounded-xl bg-[#25D366] text-white hover:bg-[#1eb457]">
                  <WhatsappIcon className="me-2 h-4 w-4" />
                  {c.whatsapp ? "محادثة واتساب" : "لا يوجد رقم"}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {canManage && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading text-navy">{editing ? "تعديل المستشار" : "إضافة مستشار"}</DialogTitle></DialogHeader>
            <div className="grid gap-4 py-2">
              <div>
                <Label>الاسم</Label>
                <Input data-testid="consultant-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 rounded-lg" placeholder="مثال: د. أحمد العلي" />
              </div>
              <div>
                <Label>التخصص / نوع العمل</Label>
                <Input data-testid="consultant-specialty" value={form.specialty} onChange={(e) => setForm((f) => ({ ...f, specialty: e.target.value }))} className="mt-1.5 rounded-lg" placeholder="مثال: استشارات أكاديمية" />
              </div>
              <div>
                <Label>رقم الواتساب</Label>
                <Input data-testid="consultant-whatsapp" value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} className="mt-1.5 rounded-lg" dir="ltr" placeholder="مثال: 966500000000 (برمز الدولة، بدون +)" />
                <p className="mt-1 flex items-center gap-1 text-xs text-slate-400"><MessageCircle className="h-3 w-3" /> أدخل الرقم بالصيغة الدولية بدون علامة + أو مسافات</p>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                <Label className="cursor-pointer">متاح الآن للاستشارات</Label>
                <Switch data-testid="consultant-available" checked={form.available} onCheckedChange={(v) => setForm((f) => ({ ...f, available: v }))} />
              </div>
            </div>
            <DialogFooter><Button data-testid="consultant-save" onClick={submit} disabled={!form.name.trim()} className="rounded-xl bg-navy hover:bg-navy-light">حفظ</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
